// src/components/ChairAdjudicator.js - Round Type and Heat Configuration
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { getDancersList } from '../utils/fieldCompatibility';
import './ChairAdjudicator.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const ROUND_TYPES = [
  'TBD',
  'Round 1',
  'Round 2',
  'Round 3',
  'Round 4',
  'Round 5',
  'Round 6',
  'Round 7',
  'Round 8',
  'Quarter-Final',
  'Semi-Final',
  'Final'
];

// Dancers Modal Component
function DancersModal({ event, onClose, dancers }) {
  const [clubs, setClubs] = useState([]);

  // Fetch clubs data for location resolution
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const response = await fetch(`https://${_host}:6003/api/${_company}/clubs`);
        if (response.ok) {
          const clubsData = await response.json();
          setClubs(clubsData);
        }
      } catch (error) {
        console.error('Error fetching clubs:', error);
      }
    };

    fetchClubs();
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dancers in Event</h3>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-event-info">
          <span className="modal-item-number">{event.itemNumber}</span>
          <span className="modal-event-name">{event.name}</span>
          <span className="modal-round-type">{event.roundType || 'TBD'}</span>
        </div>
        <div className="modal-body">
          {dancers.length > 0 ? (
            <div className="modal-dancers-list">
              <div className="dancers-header">
                <span className="dancers-count-header">
                  {dancers.length} Dancer{dancers.length !== 1 ? 's' : ''}
                </span>
              </div>
              {dancers.map((dancer, index) => (
                <DancerModalItem
                  key={index}
                  dancer={dancer}
                  clubs={clubs}
                />
              ))}
            </div>
          ) : (
            <div className="modal-empty-message">
              <p>No dancers allocated to this event</p>
              <span className="modal-hint">Dancers need to be allocated from the Administration section</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual dancer item component for the modal
function DancerModalItem({ dancer, clubs }) {
  const [dancerDetails, setDancerDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch complete dancer information including club details
  useEffect(() => {
    const fetchDancerDetails = async () => {
      if (!dancer.coupleId && !dancer.cloth_number && !dancer.clothNumber) {
        setLoading(false);
        return;
      }

      try {
        // Try to find dancer by coupleId first, then by cloth number
        let dancerId = dancer.coupleId;
        if (!dancerId) {
          // If no coupleId, try to find by cloth number
          const clothNumber = dancer.cloth_number || dancer.clothNumber;
          if (clothNumber) {
            const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
            if (response.ok) {
              const allCouples = await response.json();
              const foundDancer = allCouples.find(c =>
                (c.clothNumber === clothNumber || c.cloth_number === clothNumber) &&
                c.company === _company
              );
              if (foundDancer) {
                dancerId = foundDancer.id;
              }
            }
          }
        }

        if (dancerId) {
          const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${dancerId}`);
          if (response.ok) {
            const dancerData = await response.json();
            setDancerDetails(dancerData);
          }
        }
      } catch (error) {
        console.error('Error fetching dancer details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDancerDetails();
  }, [dancer.coupleId, dancer.cloth_number, dancer.clothNumber]);

  // Get dancer type from the dancer object
  const getDancerType = () => {
    // Use complete dancer info if available, otherwise fall back to event dancer data
    if (dancerDetails?.dancerType || dancerDetails?.dancer_type) {
      return dancerDetails.dancerType || dancerDetails.dancer_type;
    }
    return dancer.dancerType || dancer.dancer_type || dancer.type || 'Couple';
  };

  // Get dancer name with proper formatting
  const getDancerName = () => {
    // Use complete dancer info if available, otherwise fall back to event dancer data
    if (dancerDetails?.name && dancerDetails.name.trim() && !dancerDetails.name.includes('undefined')) {
      return dancerDetails.name;
    }

    // Try to compose name from lead/follow names
    const leadName = dancerDetails?.leadName || dancerDetails?.lead_name || dancer.leadName || dancer.lead_name;
    const followName = dancerDetails?.followName || dancerDetails?.follow_name || dancer.followName || dancer.follow_name;

    if (leadName && followName) {
      return `${leadName} & ${followName}`;
    } else if (leadName) {
      return leadName;
    } else if (followName) {
      return followName;
    }

    // Fall back to event dancer name
    if (dancer.name && dancer.name.trim() && !dancer.name.includes('undefined')) {
      return dancer.name;
    }

    return 'Unknown Dancer';
  };

  // Get location information following the same pattern as CouplesList
  const getLocationInfo = () => {
    if (loading) {
      return <span className="modal-location-loading">Loading...</span>;
    }

    // Use complete dancer info with club lookup (priority 1)
    if (dancerDetails) {
      // Try to find associated club
      let club = null;
      if (dancerDetails.clubId) {
        club = clubs.find(c => c.id === dancerDetails.clubId);
      }

      // If club found with location data, use club's location
      if (club && club.province && club.country) {
        return <span className="modal-location">{`${club.province}, ${club.country}`}</span>;
      } else if (club && club.country) {
        return <span className="modal-location">{club.country}</span>;
      }

      // Fall back to dancer's individual country field
      if (dancerDetails.country) {
        return <span className="modal-location">{dancerDetails.country}</span>;
      }
    }

    // Fall back to event dancer data (priority 2)
    if (dancer.country) {
      return <span className="modal-location">{dancer.country}</span>;
    }

    return <span className="modal-location">Location Unknown</span>;
  };

  return (
    <div className="modal-dancer-item">
      <div className="modal-dancer-number">{dancer.cloth_number || dancer.clothNumber}</div>
      <div className="modal-dancer-info">
        <div className="modal-dancer-name">{getDancerName()}</div>
        <div className="modal-dancer-details">
          <span className="modal-dancer-type">{getDancerType()}</span>
          <span className="modal-location-separator">•</span>
          {getLocationInfo()}
        </div>
      </div>
    </div>
  );
}

function ChairAdjudicator() {
  const { selectedCompetition } = useCompetition();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [roundType, setRoundType] = useState('');
  const [numHeats, setNumHeats] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDancersModal, setShowDancersModal] = useState(false);
  const [selectedEventForDancers, setSelectedEventForDancers] = useState(null);

  // Fetch events (filtered by selected competition)
  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      // Filter events by selected competition if available
      const filteredEvents = selectedCompetition
        ? data.filter(event => event.competitionId === selectedCompetition.id)
        : data;
      setEvents(filteredEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [selectedCompetition]); // Re-fetch when selected competition changes

  // Sort events by item number
  const sortedEvents = [...events].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  // Helper function to get dancer count for an event
  const getDancerCount = (event) => {
    const dancers = getDancersList(event);
    return dancers.length;
  };

  // Filter events based on search query
  const filteredEvents = sortedEvents.filter(event => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();

    // Search by item number
    const itemNumber = (event.itemNumber || sortedEvents.indexOf(event) + 1).toString();
    if (itemNumber.includes(query)) return true;

    // Search by event name
    if (event.name && event.name.toLowerCase().includes(query)) return true;

    // Search by round type
    if (event.roundType && event.roundType.toLowerCase().includes(query)) return true;

    return false;
  });

  const handleEdit = (event) => {
    setEditingEvent(event.id);
    setRoundType(event.roundType || 'TBD');
    setNumHeats(event.numHeats || 1);
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setRoundType('');
    setNumHeats(1);
  };

  const handleItemNumberClick = (event, e) => {
    e.stopPropagation();
    setSelectedEventForDancers(event);
    setShowDancersModal(true);
  };

  const handleCloseModal = () => {
    setShowDancersModal(false);
    setSelectedEventForDancers(null);
  };

  const handleSave = async (eventId) => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roundType,
          numHeats: roundType === 'Final' ? 1 : parseInt(numHeats, 10)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      setSuccess('Event updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      console.error('Error updating event:', err);
      setError('Failed to update event. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const toggleBroadcastState = async (eventId) => {
    try {
      // Find the current event to get its current broadcast state
      const event = events.find(e => e.id === eventId);
      const currentBroadcastState = event?.broadcast_state || 'offline';

      // Validate event before allowing broadcast (going from offline to live)
      if (currentBroadcastState !== 'live') {
        // Check if event has dancers
        const dancerCount = getDancerCount(event);
        if (dancerCount === 0) {
          setError('Cannot broadcast event: No dancers registered for this event');
          setTimeout(() => setError(null), 3000);
          return;
        }

        // Check if event has round type set (not TBD)
        if (!event.roundType || event.roundType === 'TBD') {
          setError('Cannot broadcast event: Round type not set (TBD)');
          setTimeout(() => setError(null), 3000);
          return;
        }
      }

      // If trying to take offline (live to offline), check if event is already active/running
      if (currentBroadcastState === 'live' && event.active) {
        setError('Cannot take event offline: Event is currently active/running');
        setTimeout(() => setError(null), 3000);
        return;
      }

      const newBroadcastState = currentBroadcastState === 'live' ? 'offline' : 'live';

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          broadcast_state: newBroadcastState
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update broadcast state');
      }

      setSuccess(`Event ${newBroadcastState === 'live' ? 'went live' : 'taken offline'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
      fetchEvents();
    } catch (err) {
      console.error('Error updating broadcast state:', err);
      setError('Failed to update broadcast state. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <div className="scrutineer-container">
      <div className="page-header">
        <h1>Chair Adjudicator</h1>
        <p className="page-subtitle">Configure round types and number of heats</p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {loading ? (
        <div className="loading-message">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-message">
          <p>No events available.</p>
          <p className="hint">Events must be created in Administration first.</p>
        </div>
      ) : (
        <div className="scrutineer-content">
          <div className="events-table-container">
            <div className="events-table-header">
              <div className="table-title">
                <h2>Events Configuration</h2>
                <span className="event-count">
                  {searchQuery ? `${filteredEvents.length} of ${sortedEvents.length} events` : `${sortedEvents.length} events`}
                </span>
              </div>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search by item number, event name, or round type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="events-table-scrollable">
              <table className="events-table">
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Event Name</th>
                    <th>Round Type</th>
                    <th>Heats</th>
                    <th>Dancers</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map((event) => {
                  const isEditing = editingEvent === event.id;

                  return (
                    <tr key={event.id} className={isEditing ? 'editing' : ''}>
                      <td data-label="Item #">
                        <span
                          className="item-number-badge clickable"
                          onClick={(e) => handleItemNumberClick(event, e)}
                          title="Click to view dancers"
                        >
                          {event.itemNumber || '-'}
                        </span>
                      </td>
                      <td data-label="Event Name">
                        <div className="event-name-cell">
                          <div className="event-name">{event.name}</div>
                          <div className="event-meta">
                            {event.dances?.length || 0} dances
                          </div>
                        </div>
                      </td>
                      <td data-label="Round Type">
                        {isEditing ? (
                          <select
                            value={roundType}
                            onChange={(e) => setRoundType(e.target.value)}
                            className="round-type-select"
                          >
                            {ROUND_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`round-badge ${event.roundType === 'TBD' ? 'tbd' : ''}`}>
                            {event.roundType || 'TBD'}
                          </span>
                        )}
                      </td>
                      <td data-label="Heats">
                        {isEditing ? (
                          roundType === 'Final' ? (
                            <span className="heats-display">1</span>
                          ) : (
                            <input
                              type="number"
                              value={numHeats}
                              onChange={(e) => setNumHeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
                              min="1"
                              max="10"
                              className="heats-input"
                            />
                          )
                        ) : (
                          <span className="heats-display">{event.numHeats || 1}</span>
                        )}
                      </td>
                      <td data-label="Dancers">
                        <span className="dancers-count">
                          {getDancerCount(event)}
                        </span>
                      </td>
                      <td data-label="Actions">
                        {isEditing ? (
                          <div className="action-buttons">
                            <button
                              className="save-btn"
                              onClick={() => handleSave(event.id)}
                            >
                              Save
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={handleCancel}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(event)}
                            >
                              Edit
                            </button>
                            <button
                              className={`broadcast-btn ${event.broadcast_state || 'offline'}`}
                              onClick={() => toggleBroadcastState(event.id)}
                              title={event.broadcast_state === 'live' ? 'Click to take offline' : 'Click to go live'}
                            >
                              {event.broadcast_state === 'live' ? 'RECALL' : 'BROADCAST'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredEvents.length === 0 && searchQuery && (
              <div className="no-search-results">
                <p>No events found matching "{searchQuery}"</p>
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchQuery('')}
                >
                  Clear search
                </button>
              </div>
            )}
            </div>
          </div>

          <div className="scrutineer-info">
            <h3>Chair Adjudicator Responsibilities</h3>
            <ul>
              <li>Set the round type for each event (Round 1-8, Quarter-Final, Semi-Final, Final)</li>
              <li>Configure the number of heats (automatically set to 1 for Finals)</li>
              <li>Review dancer counts to ensure proper heat distribution</li>
              <li>Events cannot be created, deleted, or reordered from this page</li>
            </ul>
          </div>

          {/* Dancers Modal */}
          {showDancersModal && selectedEventForDancers && (
            <DancersModal
              event={selectedEventForDancers}
              onClose={handleCloseModal}
              dancers={getDancersList(selectedEventForDancers)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default ChairAdjudicator;